import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Spinner } from '@blueprintjs/core';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import ProgressDialog from 'components/ProgressDialog';
import { RootState } from 'data/root.reducers';
import * as radiusParserPasswordSelectors from 'data/radiusParserPassword/radiusParserPassword.selectors';
import * as radiusParserPasswordActions from 'data/radiusParserPassword/radiusParserPassword.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import {
  RadiusParserPassword,
} from 'data/radiusParserPassword/radiusParserPassword.types';
import RadiusParserPasswordForm from '../../components/RadiusParserPasswordForm';
import { Ref } from '../../components/RadiusParserPasswordForm/RadiusParserPasswordForm';
import { radiusParserPasswordTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptTemplateFieldChanges
} from '../../utils/radiusParserPasswordTemplate.adapter';
import {
  getAuditDetailsToSubmit
} from '../../utils/radiusParserPasswordDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  radiusParserPassword: radiusParserPasswordSelectors.getRadiusParserPassword(state),
  radiusParserPasswordAdaptedTemplate: adaptTemplateVisibilityMode(radiusParserPasswordTemplate, 'edit'),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  radiusUiState: radiusParserPasswordSelectors.getRadiusParserPasswordsUIState(state),
});

const mapDispatch = {
  goToRadiusParserPasswordView: radiusParserPasswordActions.goToRadiusParserPasswordView,
  getRadiusParserPassword: radiusParserPasswordActions.getRadiusParserPassword,
  updateRadiusParserPassword: radiusParserPasswordActions.updateRadiusParserPassword,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const RadiusParserPasswordEdit = ({
  history,
  radiusParserPassword,
  radiusParserPasswordAdaptedTemplate,
  isAuditEnabled,
  userName,
  userId,
  radiusUiState,
  getRadiusParserPassword,
  goToRadiusParserPasswordView,
  updateRadiusParserPassword,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState<Partial<RadiusParserPassword>>({});
  const [template, setTemplate] = useState<FormTemplate>(radiusParserPasswordAdaptedTemplate as FormTemplate);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(radiusUiState.isLoading);
  }, [radiusUiState]);

  useEffect(() => {
    if (radiusParserPassword?.id || radiusUiState.isSaving) {
      return;
    }
    getRadiusParserPassword();
  }, [radiusUiState, radiusParserPassword, getRadiusParserPassword]);

  useEffect(() => {
    if (!radiusParserPassword?.id || !radiusParserPasswordAdaptedTemplate || radiusUiState.isSaving) {
      return;
    }

    const clonedTemplate = cloneDeep(radiusParserPasswordAdaptedTemplate);
    clonedTemplate.key = Date.now().toString();

    setTemplate(clonedTemplate);
    setFormData(cloneDeep(radiusParserPassword));
  }, [radiusUiState, radiusParserPassword, radiusParserPasswordAdaptedTemplate]);

  const handleCancel = () => goToRadiusParserPasswordView(history);

  const handleClickSave = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const radiusParserPasswordData = formRef.current.getValues();

    const payload = {
      data: radiusParserPasswordData,
      auditDetails: getAuditDetailsToSubmit(
        radiusParserPasswordData,
        radiusParserPassword as RadiusParserPassword,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    updateRadiusParserPassword((radiusParserPasswordData.id) as string, payload, history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {

    setTemplate(adaptTemplateFieldChanges(template, fieldName, value));

    const newFormData = cloneDeep(formData as RadiusParserPassword);

    setFormData({
      ...newFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (template) {
      return (
        <RadiusParserPasswordForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('radiusParserPassword.create.title')}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (loading && !radiusUiState.isSaving) {
    return <Spinner aria-label="Loading..." />;
  }

  if (!radiusParserPassword?.id && !radiusUiState.isSaving) {
    return <div />;
  }

  return (
    <div data-testid="RadiusParserPasswordEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <ProgressDialog
        open={radiusUiState.isSaving}
        title={t('radiusParserPassword.edit.saving')}
      />
    </div>
  );
};

export default withRouter(connector(RadiusParserPasswordEdit));
